import {
  AccountInfoDto,
  ConnectionStatus,
  ConnectorDto,
  ReleaseCycle,
} from "~/ui-rtk/api/types"
import type { ConnectorStatus } from "~/ui-rtk/shared/types/connectors"
import { CONNECTORS_IDS } from "../constants/sources"

export const isAccountInfo = (accountInfo?: AccountInfoDto): boolean =>
  Boolean(accountInfo?.accountName) || Boolean(accountInfo?.metric)

const excludedConnectorIds = [
  CONNECTORS_IDS.GOOGLE_KEYWORD_SEARCH,
  CONNECTORS_IDS.GOOGLE_ANALYTICS,
]

export const isConnectorExcluded = (connectorId: string) =>
  excludedConnectorIds.includes(connectorId)

/**
 * Check if a connector is an Airbyte connector
 * Airbyte connectors have type: 'AirByte' in their config
 */
export const isAirbyteConnector = (connector: ConnectorDto): boolean =>
  (connector as unknown as Record<string, unknown>)?.type === "Airbyte"

/**
 * Check if a connector is a custom connector
 * Custom connectors have type: 'Custom' in their config
 */
export const isCustomConnector = (connector: ConnectorDto): boolean =>
  (connector as unknown as Record<string, unknown>)?.type === "Custom"

export const isVisibleToCompany = (
  connector: ConnectorDto,
  companyId: string,
) => {
  if (
    connector.releaseCycle === ReleaseCycle.BETA ||
    connector.releaseCycle === ReleaseCycle.STABLE
  ) {
    return true
  }

  return Boolean(connector.metadata?.visibleToCompanyIds?.includes(companyId))
}

export const mapConnectorStatus = (
  status?: ConnectionStatus,
  hasMentionsReviewAccess = false,
): ConnectorStatus => {
  if (!status || status === ConnectionStatus.NOT_CONNECTED) {
    return "NOT_CONNECTED"
  }
  if (status === ConnectionStatus.PENDING_SETUP) {
    return "PENDING_SETUP"
  }
  if (status === ConnectionStatus.PENDING_RECONNECT) {
    return "RECONNECT"
  }
  if (
    status === ConnectionStatus.CONNECTING ||
    status === ConnectionStatus.PENDING_SCHEMA_UPDATE
  ) {
    if (hasMentionsReviewAccess) {
      return "CONNECTED"
    }
    return "PENDING"
  }
  if (status === ConnectionStatus.CONNECTED) {
    return "CONNECTED"
  }
  return "FAILED"
}

export const mapConnectorStatusWithVersionCheck = (
  status?: ConnectionStatus,
  hasMentionsReviewAccess = false,
  isVersionUpToDate = true,
): ConnectorStatus => {
  // Get the base status first
  const baseStatus = mapConnectorStatus(status, hasMentionsReviewAccess)

  // If the connector is connected but version is outdated, show UPGRADE status
  if (baseStatus === "CONNECTED" && !isVersionUpToDate) {
    return "UPGRADE"
  }

  return baseStatus
}

/**
 * Check if a connector supports ingestion (manual or hybrid delivery methods)
 * Ingestion connectors have ingestionConfig in their metadata with deliveryMethods
 */
export const isIngestionConnector = (connector: ConnectorDto): boolean => {
  const ingestionConfig = connector.metadata?.ingestionConfig

  if (!ingestionConfig?.deliveryMethods) {
    return false
  }

  return (
    ingestionConfig.deliveryMethods.includes("manual") ||
    ingestionConfig.deliveryMethods.includes("hybrid")
  )
}

/** Priority for worst-status aggregation (lower = more attention needed).
 *  NOT_CONNECTED ranks below CONNECTED so an incomplete 2nd connection
 *  doesn't mask a healthy working connection on multi-connector cards. */
const STATUS_PRIORITY: Record<string, number> = {
  PENDING_RECONNECT: 0,
  PENDING_SETUP: 1,
  CONNECTING: 2,
  PENDING_SCHEMA_UPDATE: 3,
  CONNECTED: 4,
  NOT_CONNECTED: 5,
}

/** Pick the connection with the worst status from a list (most attention needed first) */
export function pickWorstStatusConnection<T extends { status: string }>(
  connections: T[],
): T | undefined {
  if (connections.length <= 1) return connections[0] ?? undefined
  return [...connections].sort(
    (a, b) =>
      (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99),
  )[0]
}

/**
 * Computed statuses where the user must take action to make the connector
 * usable. Drives action-button visibility (Reconnect / Try again / Finish setup).
 * Compare against the computed `ConnectorStatus` string, not the raw enum.
 */
export const NEEDS_USER_ACTION_STATUSES: readonly ConnectorStatus[] = [
  "PENDING_SETUP",
  "FAILED",
  "RECONNECT",
  "NOT_CONNECTED",
]

/**
 * Computed statuses where "Start over" is offered. Excludes CONNECTED
 * (use disconnect instead) and NOT_CONNECTED (nothing to reset).
 * Compare against the computed `ConnectorStatus` string.
 */
export const RESETTABLE_STATUSES: readonly ConnectorStatus[] = [
  "PENDING_SETUP",
  "FAILED",
  "RECONNECT",
]

/** One key of a connector's metadata, whatever the metadata type is. */
const metadataField = (metadata: unknown, key: string): unknown =>
  metadata && typeof metadata === "object"
    ? (metadata as Record<string, unknown>)[key]
    : undefined

/**
 * A connector credentialed with a Meta system user: the APP-548 data fallback
 * copy of the Meta Advanced token (and legacy MD-5501 pastes until deleted).
 */
export const isMetaSystemUserConnector = (metadata: unknown): boolean =>
  metadataField(metadata, "credentialKind") === "meta_system_user"

/**
 * A system user standing in for the brand's failed login (APP-548 phase 4b),
 * marked by the API with `dataFallback: true`: "Data: system user (fallback)".
 * The brand's working login takes it back.
 */
export const isMetaDataFallbackConnector = (metadata: unknown): boolean =>
  isMetaSystemUserConnector(metadata) &&
  metadataField(metadata, "dataFallback") === true

/** `metadata.systemUserName` of a system-user connector, when the API sent it (super admins). */
export const metaSystemUserNameOf = (metadata: unknown): string | null => {
  const name = metadataField(metadata, "systemUserName")
  return typeof name === "string" && name ? name : null
}
